import { useEffect, useState } from "react";

const LAUNCH_DATE = new Date("2026-10-01T00:00:00+02:00");

function getRemaining() {
    const diff = LAUNCH_DATE.getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
    return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
        done: false,
    };
}

export default function Countdown({ variant = "default", onCtaClick }) {
    const [t, setT] = useState(getRemaining());

    useEffect(() => {
        const id = setInterval(() => setT(getRemaining()), 1000);
        return () => clearInterval(id);
    }, []);

    const units = [
        { v: t.days, l: "Dage" },
        { v: t.hours, l: "Timer" },
        { v: t.minutes, l: "Min" },
        { v: t.seconds, l: "Sek" },
    ];

    if (variant === "compact") {
        return (
            <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white px-3 py-1.5 shadow-sm" data-testid="countdown-compact">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Launch om</span>
                <div className="flex items-center gap-1 font-mono text-sm font-bold text-sky-700 tabular-nums">
                    <span>{t.days}d</span>
                    <span className="text-slate-300">·</span>
                    <span>{String(t.hours).padStart(2, "0")}t</span>
                    <span className="text-slate-300">·</span>
                    <span>{String(t.minutes).padStart(2, "0")}m</span>
                    <span className="text-slate-300">·</span>
                    <span>{String(t.seconds).padStart(2, "0")}s</span>
                </div>
            </div>
        );
    }

    return (
        <section className="py-16 md:py-20 bg-gradient-to-br from-slate-900 via-sky-950 to-slate-900 relative overflow-hidden" data-testid="countdown-section">
            <div className="absolute inset-0 bg-grid opacity-10" />
            <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-sky-500/20 blur-3xl" />
            <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-cyan-400/10 blur-3xl" />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-5">
                    <div className="inline-flex items-center gap-2 rounded-full border border-sky-400/30 bg-sky-500/10 backdrop-blur px-3 py-1 text-xs font-semibold text-sky-300">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-300" />
                        </span>
                        Launch · 1. oktober 2026
                    </div>
                    <h2 className="mt-5 font-display text-3xl md:text-5xl font-bold text-white tracking-tight leading-tight">
                        Vi launcher om
                    </h2>
                    <p className="mt-4 text-base md:text-lg text-sky-100/80 leading-relaxed">
                        Cleany går live til alle den <strong className="text-white">1. oktober 2026</strong>.
                        Tilmeld dig ventelisten og bliv én af de første kunder — ingen binding, fri opsigelse.
                    </p>
                    {onCtaClick && (
                        <button
                            onClick={onCtaClick}
                            data-testid="countdown-cta"
                            className="mt-7 inline-flex items-center gap-2 rounded-full bg-white text-sky-700 hover:bg-sky-50 font-semibold px-6 h-12 shadow-xl transition-all active:scale-[0.97]"
                        >
                            Sikr min plads i køen
                        </button>
                    )}
                </div>

                <div className="lg:col-span-7">
                    <div className="grid grid-cols-4 gap-3 md:gap-4" data-testid="countdown-grid">
                        {units.map((u) => (
                            <div
                                key={u.l}
                                className="relative rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 px-3 py-6 md:py-8 flex flex-col items-center justify-center"
                                data-testid={`countdown-${u.l.toLowerCase()}`}
                            >
                                <div className="font-display font-black text-4xl md:text-6xl text-white tabular-nums tracking-tight leading-none">
                                    {String(u.v).padStart(2, "0")}
                                </div>
                                <div className="mt-3 text-[11px] md:text-xs uppercase tracking-[0.2em] font-semibold text-sky-300/80">
                                    {u.l}
                                </div>
                                <div className="absolute inset-x-0 top-1/2 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
